import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import Navbar from "@/components/Navbar";
import PropertyCard from "@/components/PropertyCard";
import { Overline } from "@/components/shared";
import { InstallPWA } from "@/components/InstallPWA";
import {
  ArrowRight,
  FileSignature,
  CalendarCheck,
  CreditCard,
  Wrench,
  MessageSquare,
  ShieldCheck,
} from "lucide-react";

const FEATURE_ICONS = [FileSignature, CalendarCheck, CreditCard, Wrench, MessageSquare, ShieldCheck];

export default function Landing() {
  const { c } = useContent();
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const features = FEATURE_ICONS.map((icon, i) => ({
    icon,
    title: c(`feature_${i + 1}_title`),
    desc: c(`feature_${i + 1}_desc`),
  }));

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 lg:pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up opacity-0">
            <Overline>{c("hero_overline")}</Overline>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-ink leading-[1.05]">
              {c("hero_title")}
            </h1>
            <p className="mt-6 text-base text-mute leading-relaxed max-w-lg">{c("hero_subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/browse" data-testid="hero-browse-btn" className="inline-flex items-center gap-2 bg-forest text-white px-6 py-3 rounded-lg font-medium hover:bg-forest-hover transition-colors shadow-sm">
                {c("hero_cta_primary")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/register" data-testid="hero-landlord-btn" className="inline-flex items-center gap-2 bg-white border border-line text-ink px-6 py-3 rounded-lg font-medium hover:bg-white/50 transition-colors">
                {c("hero_cta_secondary")}
              </Link>
            </div>
          </div>
          <div className="relative animate-fade-up opacity-0" style={{ animationDelay: "120ms" }}>
            <img src={c("hero_image")} alt="Modern home" className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg border border-line p-4 hidden sm:block">
              <p className="text-xs text-mute">{c("hero_badge_label")}</p>
              <p className="font-heading font-semibold text-forest">{c("hero_badge_value")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 -mt-4 mb-4">
        <InstallPWA variant="banner" />
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="max-w-2xl">
          <Overline>{c("features_overline")}</Overline>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-ink">
            {c("features_heading")}
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div key={i} className="bg-white border border-line rounded-xl p-6 hover:shadow-[0_8px_24px_rgba(26,26,26,0.08)] transition-all duration-300">
              <div className="h-11 w-11 rounded-xl bg-bone flex items-center justify-center">
                <f.icon className="h-5 w-5 text-forest" strokeWidth={1.75} />
              </div>
              <h3 className="mt-4 font-heading font-medium text-ink text-lg">{f.title}</h3>
              <p className="mt-1.5 text-sm text-mute leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {properties.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <Overline>{c("featured_overline")}</Overline>
              <h2 className="mt-3 text-2xl sm:text-3xl font-medium tracking-tight text-ink">{c("featured_heading")}</h2>
            </div>
            <Link to="/browse" className="text-sm font-medium text-forest hover:underline flex items-center gap-1">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((p, i) => (
              <PropertyCard key={p.id} property={p} index={i} />
            ))}
          </div>
        </section>
      )}

      <footer className="border-t border-line mt-8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-heading font-semibold text-ink">{c("brand_name")}</span>
          <span className="text-sm text-mute">{c("footer_tagline")}</span>
        </div>
      </footer>
    </div>
  );
}
